import { Fragment, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { DrawerBodyWrapper, TOAST_TYPE } from '@base'
import { Button } from '@baseV2'
import { strings, buyerTermsTypes, supplierTermsTypes, buyerConditionsByTerm, supplierConditionsByTerm, paymentTermTypeOptions } from '@constants'
import { showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { Delete } from '@material-ui/icons'
import { isPositiveIntegerString, preventNonIntegerKeyDown } from '@utils'
import { BodyCell, CellInput, CellSelect, DeleteButton, EmptyState, FooterActions, FooterWrapper, HeaderCell, HelperText, MethodChip, PrimaryButton, RowActions, TermsGrid, TypeCellContent } from './stylesV2'

// canonical section order — rows are always grouped/inserted in this sequence
const SECTION_ORDER = ['ADVANCE', 'BG', 'CREDIT', 'LC']
const sectionIndex = (paymentTerm) => {
	const idx = SECTION_ORDER.indexOf(paymentTerm)
	return idx === -1 ? SECTION_ORDER.length : idx // unknown types sit at the end
}

// a row only counts once it carries a percentage; rows without one are dropped on submit, so
// clearing the percentage is how an existing term is removed (parent rows have no delete button)
const isActiveRow = (row) => row?.percentage !== '' && Number(row?.percentage) > 0

// required for every active row
const INTERDEPENDENT_FIELDS = ['tenure_in_days', 'percentage', 'condition']

const PaymentTermsV2 = ({ edit = false, data, callbackFunction, viewOnly, isBuyer = false, isSupplier = false }) => {
	const dispatch = useDispatch()

	// isBuyer takes precedence, same as the v1 component
	const termOptions = isBuyer ? buyerTermsTypes : isSupplier ? supplierTermsTypes : []
	const conditionsByTerm = isBuyer ? buyerConditionsByTerm : supplierConditionsByTerm

	// unique, stable id per row so multiple rows of the same payment_term coexist
	const idRef = useRef(0)
	const nextId = () => (idRef.current += 1)

	// one row per existing entry, preserving saved order and duplicates …
	const deriveRows = () => {
		const dataRows = (data ?? []).map((item) => ({
			id: nextId(),
			payment_term: item?.payment_term,
			label: termOptions.find((term) => term.key === item?.payment_term)?.label ?? item?.payment_term,
			type: item?.type ?? 'IFC',
			tenure_in_days: item?.tenure_in_days ?? '',
			percentage: item?.percentage ?? '',
			condition: item?.condition ?? '',
		}))
		// … then ensure every available payment term type is visible with at least one row
		const missingRows = termOptions
			.filter((term) => !dataRows.some((row) => row.payment_term === term.key))
			.map((term) => ({ id: nextId(), payment_term: term.key, label: term.label, type: 'IFC', tenure_in_days: '', percentage: '', condition: '' }))
		// group everything into the canonical section order, preserving each type's internal order
		const allRows = [...dataRows, ...missingRows]
		return allRows.slice().sort((a, b) => sectionIndex(a.payment_term) - sectionIndex(b.payment_term))
	}
	const [rows, setRows] = useState(deriveRows)

	const updateRow = (id, field) => (value) => {
		setRows((_rows) =>
			_rows.map((row) => {
				if (row.id !== id) return row
				const updated = { ...row, [field]: value, [`${field}Error`]: undefined }
				// percentage cleared — the row is on its way out, so drop any stale required-field errors
				return isActiveRow(updated)
					? updated
					: { ...updated, tenure_in_daysError: undefined, percentageError: undefined, conditionError: undefined }
			})
		)
	}

	// pasting a non-positive-integer into tenure/percentage is rejected outright and flags an inline error
	const handleIntegerPaste = (id, field) => (e) => {
		const pasted = e.clipboardData.getData('text').trim()
		if (isPositiveIntegerString(pasted)) return
		e.preventDefault()
		setRows((_rows) => _rows.map((row) => (row.id === id ? { ...row, [`${field}Error`]: strings('must_be_positive_whole_number') } : row)))
	}

	// insert a new row of the chosen type into its section: after the last row of the same
	// (or earlier) section, before the first row of a later section — appended if none exists
	const addRow = (term) => {
		const newRow = { id: nextId(), payment_term: term.key, label: term.label, type: 'IFC', tenure_in_days: '', percentage: '', condition: '' }
		setRows((_rows) => {
			const target = sectionIndex(term.key)
			const insertAt = _rows.findIndex((row) => sectionIndex(row.payment_term) > target)
			return insertAt === -1 ? [..._rows, newRow] : [..._rows.slice(0, insertAt), newRow, ..._rows.slice(insertAt)]
		})
	}

	const deleteRow = (id) => {
		setRows((_rows) => _rows.filter((row) => row.id !== id))
	}

	const submitHandler = () => {
		// tenure/condition are required only on rows that carry a percentage; the rest are ignored
		let hasRowError = false
		const validatedRows = rows.map((row) => {
			if (!isActiveRow(row)) {
				return { ...row, tenure_in_daysError: undefined, percentageError: undefined, conditionError: undefined }
			}
			const rowErrors = {}
			INTERDEPENDENT_FIELDS.forEach((field) => {
				if (row[field] === '') {
					rowErrors[`${field}Error`] = strings(field, 'required')
					hasRowError = true
				} else {
					rowErrors[`${field}Error`] = undefined
				}
			})
			return { ...row, ...rowErrors }
		})
		if (hasRowError) {
			setRows(validatedRows)
			return
		}

		const termList = validatedRows
			.filter(isActiveRow)
			.map((row) => ({
				payment_term: row.payment_term,
				...(row.payment_term === 'LC' ? { type: row.type } : {}),
				tenure_in_days: Number(row.tenure_in_days),
				condition: row.condition,
				percentage: Number(row.percentage),
			}))
		const total = termList.reduce((acc, item) => acc + item.percentage, 0)
		if (total !== 100) {
			dispatch(showToast(true, strings('payment_terms_100'), { type: TOAST_TYPE.ERROR }))
			return
		}
		callbackFunction(termList)
	}

	// keep Submit/Update inactive while any row is currently showing an error (required-field or paste validation)
	const rowsHaveError = rows.some((row) => row.tenure_in_daysError || row.percentageError || row.conditionError)

	return (
		<>
			<DrawerBodyWrapper>
				<TermsGrid>
					<HeaderCell>{strings('method')}</HeaderCell>
					<HeaderCell>{strings('tenure_in_days')}</HeaderCell>
					<HeaderCell>{strings('percentage')}</HeaderCell>
					<HeaderCell>{strings('condition')}</HeaderCell>
					<HeaderCell>{strings('action')}</HeaderCell>
					{rows.map((row, index) => {
						const conditionOptions = conditionsByTerm[row.payment_term] ?? []
						// the first row of each type is the parent (can add more rows); the rest are deletable children
						const isParent = rows.findIndex((r) => r.payment_term === row.payment_term) === index
						return (
							<Fragment key={row.id}>
								<BodyCell $noHPad>
									<TypeCellContent>
										<MethodChip>{row.label}</MethodChip>
										{row.payment_term === 'LC' && (
											<CellSelect
												id={`type-${row.id}`}
												data={paymentTermTypeOptions}
												primaryKey='key'
												displayKey='label'
												disabled={viewOnly}
												value={paymentTermTypeOptions.find((option) => option.key === row.type) ?? null}
												onChange={(option) => updateRow(row.id, 'type')(option.key)}
											/>
										)}
									</TypeCellContent>
								</BodyCell>
								<BodyCell>
									<CellInput
										id={`tenure-${row.id}`}
										type='number'
										value={row.tenure_in_days}
										disabled={viewOnly}
										error={row.tenure_in_daysError}
										onKeyDown={preventNonIntegerKeyDown}
										onPaste={handleIntegerPaste(row.id, 'tenure_in_days')}
										onChange={(e) => updateRow(row.id, 'tenure_in_days')(e.target.value.replace(/[^0-9]/g, ''))}
									/>
								</BodyCell>
								<BodyCell>
									<CellInput
										id={`percentage-${row.id}`}
										type='number'
										value={row.percentage}
										disabled={viewOnly}
										error={row.percentageError}
										onKeyDown={preventNonIntegerKeyDown}
										onPaste={handleIntegerPaste(row.id, 'percentage')}
										onChange={(e) => updateRow(row.id, 'percentage')(e.target.value.replace(/[^0-9]/g, ''))}
									/>
								</BodyCell>
								<BodyCell>
									<CellSelect
										id={`condition-${row.id}`}
										placeholder={strings('select', 'condition')}
										data={conditionOptions}
										primaryKey='key'
										displayKey='label'
										disabled={viewOnly}
										error={row.conditionError}
										value={conditionOptions.find((option) => option.key === row.condition) ?? null}
										onChange={(option) => updateRow(row.id, 'condition')(option.key)}
									/>
								</BodyCell>
								<BodyCell>
									{!viewOnly && (
										<RowActions>
											{isParent ? (
												<Button
													variant='outline'
													color='neutral'
													size='small'
													onClick={() => addRow({ key: row.payment_term, label: row.label })}
												>
													{`+`}
												</Button>
											) : (
												<DeleteButton type='button' aria-label={strings('delete')} onClick={() => deleteRow(row.id)}>
													<Delete />
												</DeleteButton>
											)}
										</RowActions>
									)}
								</BodyCell>
							</Fragment>
						)
					})}
				</TermsGrid>
				{rows.length === 0 && <EmptyState>{strings('add_payment_term_to_begin')}</EmptyState>}
				<HelperText>{strings('payment_terms_total_100_helper')}</HelperText>
			</DrawerBodyWrapper>
			<FooterWrapper>
				<FooterActions>
					<Button variant='outline' color='danger' onClick={() => dispatch(toggleDrawer(false))}>
						{strings('cancel')}
					</Button>
					<PrimaryButton onClick={submitHandler} disabled={viewOnly || rowsHaveError}>
						{edit ? strings('update') : strings('submit')}
					</PrimaryButton>
				</FooterActions>
			</FooterWrapper>
		</>
	)
}

export default PaymentTermsV2
